import { useEffect, useMemo, useRef } from 'react';
import { DataSourceRecord } from 'src/models/common/DataSourceRecord';
import { extractMDAValueFromMDAWorksheetResults, groupMDARecordsByWorksheetAndDatasourceId } from './Utils';
import { parseExcelFromArrayBuffer, ExcelRowConverter } from 'src/utils/ExcelUtils';
import ErrorUtils from "src/utils/ErrorUtils";
import CONSTANTS from 'src/utils/constants';

/**
 * MDA Results row data with simplified array-based structure
 */
interface MDAResultsRow {
    row: (string | number | undefined)[];
}

/**
 * Simplified converter for MDA results data
 */
const mdaResultsConverter: ExcelRowConverter<MDAResultsRow> = (rowData: any[], headers: string[], rowIndex: number): MDAResultsRow => {
    // Convert row data to simplified array format
    const row: (string | number | undefined)[] = headers.map((header, columnIndex) => {
        const cellValue = rowData[columnIndex];
        
        // Handle different data types
        if (cellValue === null || cellValue === undefined) {
            return undefined;
        }
        
        return cellValue;
    });

    return {
        row
    };
};

interface UseMDAValueLoaderProps {
    dataSourceRecords: DataSourceRecord[];
    onRecordUpdate: (datasourceId: string, updates: Partial<DataSourceRecord>) => void;
    onBatchUpdate?: (updates: Array<{ datasourceId: string; update: Partial<DataSourceRecord> }>) => void;
    refreshTrigger?: DataSourceRecord[]; // Trigger to clear processed records
}

export function useMDAValueLoader({ 
    dataSourceRecords,
    onRecordUpdate,
    onBatchUpdate,
    refreshTrigger
}: UseMDAValueLoaderProps) {

    // Track processed record IDs to prevent duplicate processing
    const processedRecordsRef = useRef(new Set<string>());

    // Clear processed records when refresh is triggered
    useEffect(() => {
        processedRecordsRef.current.clear();
    }, [refreshTrigger]);

    // Filter records that need MDA value loading
    const datasourceRecordsToBeUpdated = useMemo(() =>
        dataSourceRecords.filter(record =>
            record.datasource === CONSTANTS.DATA_SOURCE_TYPES.MULTIDIMENSIONAL_ALLOCATION_WORKSHEET &&
            !!record.multiAllocationWorksheetId && // Only load when existing record.
            record.multiAllocationWorksheetDatasourceResultsFileURL &&
            !processedRecordsRef.current.has(record.datasourceId) // Don't reprocess
        ), [dataSourceRecords]);

    // Group records by worksheetId + datasourceId, use any S3 URL from the group as map key
    const urlToRecordsMap = useMemo(() => {
        datasourceRecordsToBeUpdated.forEach(record => {
            processedRecordsRef.current.add(record.datasourceId);
        });
        return groupMDARecordsByWorksheetAndDatasourceId(datasourceRecordsToBeUpdated);
    }, [datasourceRecordsToBeUpdated]);

    useEffect(() => {
        const fetchAllMDAData = async () => {
            // Collect all loading state updates first
            const loadingUpdates: Array<{ datasourceId: string; update: Partial<DataSourceRecord> }> = [];
            Array.from(urlToRecordsMap.values()).flat().forEach(record => {
                loadingUpdates.push({
                    datasourceId: record.datasourceId,
                    update: { balancePullInProgress: true }
                });
            });

            // Batch dispatch loading states
            if (onBatchUpdate) {
                onBatchUpdate(loadingUpdates);
            } else {
                requestAnimationFrame(() => {
                    loadingUpdates.forEach(({ datasourceId, update }) => {
                        onRecordUpdate(datasourceId, update);
                    });
                });
            }
            
            // OPTIMIZATION 1: Fetch all S3 files in parallel instead of sequential
            const fetchPromises = Array.from(urlToRecordsMap.entries()).map(async ([s3Url, records]) => {
                try {
                    const response = await fetch(s3Url);
                    if (!response.ok) {
                        throw new Error(`Failed to load excel sheet, received http: ${response.status}`);
                    }
                    const arrayBuffer = await response.arrayBuffer();
                    const parseResult = parseExcelFromArrayBuffer<MDAResultsRow>(
                        arrayBuffer,
                        mdaResultsConverter,
                        { defaultValue: "", sheetIndex: 1, dataStartRowIndex: 0 }
                    );

                    // OPTIMIZATION 2: Batch process all records for this S3 file
                    const updates: Array<{ datasourceId: string; update: Partial<DataSourceRecord> }> = [];
                    
                    records.forEach(record => {
                        try {
                            const calcResult = extractMDAValueFromMDAWorksheetResults(
                                parseResult.data,
                                record.multiAllocationWorksheetValueIdentifier!
                            );
                            
                            if (calcResult.error) {
                                const errors = new Map<string, string>();
                                errors.set('MDA', calcResult.error);
                                updates.push({
                                    datasourceId: record.datasourceId,
                                    update: { errors, balancePullInProgress: false }
                                });
                            } else {
                                updates.push({
                                    datasourceId: record.datasourceId,
                                    update: {
                                        value: calcResult.calculatedValue.toString(),
                                        balancePullInProgress: false
                                    }
                                });
                            }
                        } catch (error) {
                            const errors = new Map<string, string>();
                            errors.set('MDA', ErrorUtils.getMessage(error));
                            updates.push({
                                datasourceId: record.datasourceId,
                                update: { errors, balancePullInProgress: false }
                            });
                        }
                    });

                    return updates;

                } catch (error) {
                    const errorMessage = ErrorUtils.getMessage(error);
                    return records.map(record => {
                        const errors = new Map<string, string>();
                        errors.set('MDA', errorMessage);
                        return {
                            datasourceId: record.datasourceId,
                            update: { errors, balancePullInProgress: false }
                        };
                    });
                }
            });

            // OPTIMIZATION 3: Wait for all S3 files to complete, then batch update Redux
            const allUpdates = await Promise.all(fetchPromises);
            const flattenedUpdates = allUpdates.flat();
            
            // OPTIMIZATION 4: Apply all updates in a single batch to minimize re-renders
            if (onBatchUpdate) {
                onBatchUpdate(flattenedUpdates);
            } else {
                requestAnimationFrame(() => {
                    flattenedUpdates.forEach(({ datasourceId, update }) => {
                        onRecordUpdate(datasourceId, update);
                    });
                });
            }
        };

        if (urlToRecordsMap.size > 0) {
            fetchAllMDAData();
        }
    }, [urlToRecordsMap, onRecordUpdate, onBatchUpdate]);
}
